'use client';
import { useEffect, useState, useRef } from 'react';
import {
  Box,
  Stack,
  Card,
  Typography,
  Divider,
  CircularProgress,
  IconButton,
  Button,
  FormControl,
  Select,
  MenuItem,
  Chip,
  LinearProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import RouteIcon from '@mui/icons-material/Route';
import CloseIcon from '@mui/icons-material/Close';
import ListAltIcon from '@mui/icons-material/ListAlt';
// import VisibilityIcon from '@mui/icons-material/Visibility';
import FilterListIcon from '@mui/icons-material/FilterList';
import { Tabs, Tab, Tooltip, Dialog, DialogTitle, DialogContent } from '@mui/material';

import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

import { fetchRetase } from '../../../stores/retaseStore';
import { useHistoryWayPointList } from '../../../stores/historyWayPointStore';
import { useRouteTripAll } from '../../../stores/routeTripStore';
import { useLanguage } from '../../../utils/i18n';
import { timezone } from '../../../utils/timeZone';
import GuideRouteDialog from '../../master-data/master-route/GuideRouteDialog';
import MasterRoutePage from '../../master-data/master-route/MasterRoutePage';
import { getWpTimeEpoch } from '../../../utils/mapTripUtils';

const SENSOR_CONFIG = {
  DRB: { label: 'Brake (DRB)', color: '#F44336' },
  DRL: { label: 'Turn Left (DRL)', color: '#4CAF50' },
  DRR: { label: 'Turn Right (DRR)', color: '#2196F3' },
  DUM: { label: 'Dump (DUM)', color: '#FF9800' },
  DPB: { label: 'DPB', color: '#9C27B0' },
  OFF: { label: 'Engine OFF', color: '#9E9E9E' },
  '': { label: 'Normal Route', color: '#1d89d5' }
};
const SENSOR_TYPES = ['DRB', 'DRL', 'DRR', 'DUM', 'DPB', 'OFF', ''];

const formatUI_DateTime = (input) => {
  try {
    if (!input) return '-';
    const d = new Date(input);
    if (isNaN(d.getTime())) return String(input);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    const hh = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${dd}/${mm}/${yy} ${hh}:${min}`;
  } catch {
    return String(input ?? '-');
  }
};

const formatDurationStr = (tripItem) => {
  let seconds = Number(tripItem.duration);
  if (!seconds || isNaN(seconds)) {
    if (tripItem.start_time && tripItem.end_time) {
      seconds = (new Date(tripItem.end_time).getTime() - new Date(tripItem.start_time).getTime()) / 1000;
    } else {
      return '-';
    }
  }
  if (seconds <= 0) return '-';

  const totalMins = Math.floor(seconds / 60);
  if (totalMins < 60) return `${totalMins} Min`;

  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;
  return `${hours} h ${mins > 0 ? `${mins} Min` : ''}`.trim();
};

// Prioritaskan start_wp_time sesuai permintaan backend.
// Memakai parser bersama (mapTripUtils) agar urutan di list, chart, dan map identik.
const getEpochFromWp = (wp) => getWpTimeEpoch(wp) ?? 0;

const extractSensorType = (wp) => {
  const sType = wp.sensor_type ?? wp.sensor ?? wp.event ?? wp.type ?? '';
  return String(sType).toUpperCase();
};

const enrichWaypointsWithSensorData = (waypoints, tripList = []) => {
  // Pre-process tripList to find the exact time the vehicle unloads (leaves destination spatial)
  const effectiveTrips = (tripList || []).map((trip) => {
    if (!trip.start_time) return { sTime: 0, loadedEndTime: 0 };

    const sTime = dayjs(trip.start_time).valueOf();
    const eTime = trip.end_time ? dayjs(trip.end_time).valueOf() : Infinity;

    const routes = trip.routes || [];
    const spatialNames = routes.map((v) => (v.spatial_name || v.spatialName || '').toLowerCase().trim()).filter(Boolean);
    const destSpatialName = spatialNames.length > 0 ? spatialNames[spatialNames.length - 1] : null;

    let loadedEndTime = eTime;

    if (destSpatialName) {
      for (let i = 0; i < waypoints.length; i++) {
        const wp = waypoints[i];
        const wpEpoch = getEpochFromWp(wp);

        if (wpEpoch >= sTime && wpEpoch <= eTime) {
          const wpSpatialName = String(
            wp.spatial_description ||
              wp.info?.spatial_description ||
              wp.polygon_type_name ||
              wp.info?.polygon_type_name ||
              wp.spatial_name ||
              wp.info?.spatial_name ||
              ''
          )
            .toLowerCase()
            .trim();

          // Strict match to prevent false positives
          const isAtDest =
            wpSpatialName && destSpatialName && (wpSpatialName === destSpatialName || wpSpatialName.includes(destSpatialName));

          if (isAtDest) {
            // Begitu menyentuh tujuan akhir (masuk spasial), langsung hentikan deteksi (muatan dianggap selesai)
            loadedEndTime = wpEpoch;
            break;
          }
        }
      }
    }
    return { sTime, loadedEndTime };
  });

  return waypoints.map((wp) => {
    const sType = extractSensorType(wp);
    const validConfigKey = SENSOR_CONFIG[sType] ? sType : '';
    const config = SENSOR_CONFIG[validConfigKey];

    let is_in_retase = false;
    const wpEpoch = getEpochFromWp(wp);

    if (effectiveTrips.length > 0) {
      for (const trip of effectiveTrips) {
        if (trip.sTime > 0 && wpEpoch >= trip.sTime && wpEpoch <= trip.loadedEndTime) {
          is_in_retase = true;
          break;
        }
      }
    }

    let modifiedWp = { ...wp };

    if (!is_in_retase) {
      if (modifiedWp.spatial_max_speed) {
        modifiedWp.spatial_max_speed = 0;
      }
      if (modifiedWp.info?.spatial_max_speed) {
        modifiedWp.info = { ...modifiedWp.info, spatial_max_speed: 0 };
      }
    }

    return { ...modifiedWp, _computedSensorType: validConfigKey, sensorLabel: config.label, lineColor: config.color, is_in_retase };
  });
};

const categorizeWaypoints = (waypoints) => {
  const categorized = { drb: [], drl: [], drr: [], dum: [], dpb: [], off: [], normal: [], all: waypoints };
  waypoints.forEach((wp) => {
    const sType = wp._computedSensorType || '';
    if (sType === 'DRB') categorized.drb.push(wp);
    else if (sType === 'DRL') categorized.drl.push(wp);
    else if (sType === 'DRR') categorized.drr.push(wp);
    else if (sType === 'DUM') categorized.dum.push(wp);
    else if (sType === 'DPB') categorized.dpb.push(wp);
    else if (sType === 'OFF') categorized.off.push(wp);
    else categorized.normal.push(wp);
  });
  return categorized;
};

export default function TripHistoryList({ vehicle, onBack, onWaypointsLoaded, isFullscreen = false, onLoadingStateChange }) {
  const { t } = useLanguage();
  const [rangeMode, setRangeMode] = useState('today');
  const [toDate, setToDate] = useState(() => dayjs().second(59));
  const [fromDate, setFromDate] = useState(() => dayjs().startOf('day').second(0));
  const [dateError, setDateError] = useState('');
  const [showPeriodConfig, setShowPeriodConfig] = useState(true);

  const [searchParams, setSearchParams] = useState(null);

  const [expanded, setExpanded] = useState({});
  const [detailsExpanded, setDetailsExpanded] = useState({});

  const [activeTab, setActiveTab] = useState('All');
  const [showRouteDialog, setShowRouteDialog] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  const [tripList, setTripList] = useState([]);
  const [isLoadingTrips, setIsLoadingTrips] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');
  const [activePlayId, setActivePlayId] = useState(null);
  const [activeTimeRange, setActiveTimeRange] = useState({ start: 0, end: 0 });

  const vehicleId = vehicle?.vehicle ?? vehicle?.id ?? vehicle?.vehicle_id ?? null;
  const [companyId, setCompanyId] = useState(null);

  useEffect(() => {
    const cid = vehicle?.raw?.company ?? vehicle?.vehicle?.company ?? vehicle?.info?.company ?? null;
    if (cid) {
      setCompanyId(cid);
    } else if (typeof window !== 'undefined') {
      setCompanyId(localStorage.getItem('companyId'));
    }
  }, [vehicle]);

  const validateDateRange = (startDayjs, endDayjs) => {
    if (!startDayjs || !endDayjs) {
      setDateError(t('mapsTracking.dateCannotBeEmpty', 'Date cannot be empty'));
      return false;
    }
    const diff = endDayjs.diff(startDayjs, 'day');

    if (diff > 7) {
      setDateError(t('mapsTracking.maxDateRange7Days', 'Maximum time span is 7 days'));
      return false;
    } else if (diff < 0) {
      setDateError(t('mapsTracking.startDateCannotExceedEndDate', 'Start time cannot exceed end time'));
      return false;
    } else {
      setDateError('');
      return true;
    }
  };

  const handleRangeModeChange = (e) => {
    const mode = e.target.value;
    setRangeMode(mode);

    let start = fromDate;
    let end = toDate;

    if (mode === 'today') {
      start = dayjs().startOf('day').second(0);
      end = dayjs().second(59);
    } else if (mode === 'lastWeek') {
      start = dayjs().subtract(7, 'day').second(0);
      end = dayjs().second(59);
    }

    setFromDate(start);
    setToDate(end);
    validateDateRange(start, end);
  };

  const handleSearch = () => {
    if (validateDateRange(fromDate, toDate)) {
      setSearchParams({
        start: fromDate,
        end: toDate,
        mode: rangeMode,
        timestamp: Date.now() // Unique ID to force refresh on identical searches
      });
      setShowPeriodConfig(false);
    }
  };

  const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
  const wpStartTimeParam = searchParams ? searchParams.start.format('YYYY-MM-DD HH:mm:ss') : '';
  const wpEndTimeParam = searchParams ? searchParams.end.format('YYYY-MM-DD HH:mm:ss') : '';

  const [loadingProgressWp, setLoadingProgressWp] = useState(0);

  const {
    data: fullRawWaypoints = [],
    isLoading: isLoadingWp,
    isError: isErrorWp
  } = useHistoryWayPointList(
    searchParams ? vehicleId : undefined,
    companyId,
    SENSOR_TYPES,
    wpStartTimeParam,
    wpEndTimeParam,
    dateError,
    (progress) => setLoadingProgressWp(progress),
    tz
  );

  const { data: allRoutes = [] } = useRouteTripAll(0, 10000, '', '', companyId);

  useEffect(() => {
    if (onLoadingStateChange) {
      onLoadingStateChange(isLoadingWp, loadingProgressWp);
    }
  }, [isLoadingWp, loadingProgressWp, onLoadingStateChange]);
  useEffect(() => {
    const fetchTrips = async () => {
      if (!searchParams || !vehicleId || !companyId) return;

      const { start: searchStart, end: searchEnd } = searchParams;

      setIsLoadingTrips(true);
      setTripList([]);
      setActivePlayId(null);
      if (onWaypointsLoaded) onWaypointsLoaded({ all: [] });

      try {
        const vIdsStr = String(vehicleId);

        const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
        let dates = [];
        let curr = searchStart.clone().startOf('day');
        const end = searchEnd.clone().startOf('day');

        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          const currentLoopDay = curr.clone();
          let loopStartTime = currentLoopDay.startOf('day');
          let loopEndTime = currentLoopDay.endOf('day');

          if (currentLoopDay.isSame(searchStart, 'day')) loopStartTime = searchStart;
          if (currentLoopDay.isSame(searchEnd, 'day')) loopEndTime = searchEnd;

          dates.push({
            startStr: loopStartTime.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]'),
            endStr: loopEndTime.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]')
          });

          curr = curr.add(1, 'day');
        }

        const totalDates = dates.length;
        let completedDates = 0;
        setLoadingProgress(0);
        setLoadingText('Preparing data...');

        let allTrips = [];
        const chunkSize = 5;

        for (let i = 0; i < totalDates; i += chunkSize) {
          const chunk = dates.slice(i, i + chunkSize);
          const chunkPromises = chunk.map(async (dateRange) => {
            try {
              const queryParams = {
                vehicleId: vIdsStr,
                companyId: String(companyId),
                tz,
                startTime: dateRange.startStr,
                endTime: dateRange.endStr
              };

              const response = await fetchRetase(queryParams);
              const apiDataRaw = response?.data?.retase ?? [];

              const uniqueTripsMap = new Map();
              apiDataRaw.forEach((tripData) => {
                const routes = tripData.routes || [];
                const details = tripData.details || [];

                const spatialNames = routes.map((v) => v.spatial_name || v.spatialName).filter(Boolean);
                const routeTitle = spatialNames.length > 0 ? spatialNames.join(' - ') : `Route ID: ${tripData.route_trip_id}`;

                let earliestStart = null;
                let latestEnd = null;
                details.forEach((d) => {
                  const st = dayjs(d.start);
                  const en = dayjs(d.end);
                  if (!earliestStart || st.isBefore(earliestStart)) earliestStart = st;
                  if (!latestEnd || en.isAfter(latestEnd)) latestEnd = en;
                });

                const finalStart = earliestStart ? earliestStart.format('YYYY-MM-DD HH:mm:ss') : 'no-start';
                const finalEnd = latestEnd ? latestEnd.format('YYYY-MM-DD HH:mm:ss') : 'no-end';

                const uniqueKey = `${routeTitle}_${finalStart}_${finalEnd}`;
                if (!uniqueTripsMap.has(uniqueKey)) {
                  uniqueTripsMap.set(uniqueKey, tripData);
                }
              });
              const apiData = Array.from(uniqueTripsMap.values());

              const processedTrips = [];

              // Ekstrak data berdasarkan struktur baru API (Support Multi-Details)
              apiData.forEach((tripData) => {
                const sortedRoutes = (tripData.routes || []).sort((a, b) => a.sequence_number - b.sequence_number);
                const detailsArray = tripData.details || [];

                // Looping setiap detail, jadi 1 detail = 1 Card
                detailsArray.forEach((dt) => {
                  processedTrips.push({
                    route_trip_id: tripData.route_trip_id,
                    total_trips: tripData.total_trips || 1,
                    routes: sortedRoutes,
                    start_time: dt.start || null,
                    end_time: dt.end || null,
                    duration: dt.dur || 0,
                    license_plate: vehicle?.fleet_name || vehicle?.vehicle_license_plate || 'Unknown'
                  });
                });
              });

              return processedTrips;
            } catch (err) {
              console.error(`Failed to fetch trips for date ${dateRange.startStr}`, err);
              return [];
            } finally {
              completedDates++;
              setLoadingProgress(Math.round((completedDates / totalDates) * 100));
              setLoadingText(`Search ritase data (${completedDates}/${totalDates})`);
            }
          });

          const dailyResults = await Promise.all(chunkPromises);
          allTrips.push(...dailyResults.flat());
        }

        // Sort berdasarkan waktu mulai
        allTrips.sort((a, b) => new Date(a.start_time || 0).getTime() - new Date(b.start_time || 0).getTime());

        setTripList(allTrips);
      } catch (err) {
        console.error('Failed to fetch ritase trips', err);
      } finally {
        setIsLoadingTrips(false);
      }
    };

    fetchTrips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, vehicleId, companyId, onWaypointsLoaded]);

  const togglePlay = (trip, index) => {
    const tripId = `trip-${index}`;
    if (activePlayId === tripId) {
      setActivePlayId(null);
      setActiveTimeRange({ start: 0, end: 0 });
      if (onWaypointsLoaded) onWaypointsLoaded({ all: [] });
    } else {
      setActivePlayId(tripId);
      setActiveTimeRange({
        start: new Date(trip.start_time).getTime(),
        end: new Date(trip.end_time).getTime()
      });
    }
  };

  const toggleExpand = (id, event) => {
    const willBeExpanded = !expanded[id];
    setExpanded((p) => ({ ...p, [id]: !p[id] }));

    if (willBeExpanded && event && event.currentTarget) {
      const cardElement = event.currentTarget.closest('.trip-card');
      if (cardElement) {
        setTimeout(() => {
          cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
      }
    }
  };
  const toggleDetails = (id, event) => {
    const willBeExpanded = !detailsExpanded[id];
    setDetailsExpanded((p) => ({ ...p, [id]: !p[id] }));

    if (willBeExpanded && event && event.currentTarget) {
      const cardElement = event.currentTarget.closest('.trip-card');
      if (cardElement) {
        setTimeout(() => {
          cardElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 200);
      }
    }
  };

  const prevWpIdRef = useRef(null);

  useEffect(() => {
    if (!Array.isArray(fullRawWaypoints) || fullRawWaypoints.length === 0) {
      if (prevWpIdRef.current !== 'empty' && !isLoadingWp) {
        if (onWaypointsLoaded) onWaypointsLoaded({ all: [] });
        prevWpIdRef.current = 'empty';
      }
      return;
    }

    if (isLoadingTrips) {
      // Tunggu sampai data ritase selesai dimuat agar bisa difilter overspeed-nya
      return;
    }

    const searchId = searchParams?.timestamp || '';

    if (!activePlayId) {
      const currentWpId = `full-${fullRawWaypoints.length}-${searchId}-${tripList.length}`;
      if (prevWpIdRef.current !== currentWpId) {
        const sortedAll = [...fullRawWaypoints].sort((a, b) => getEpochFromWp(a) - getEpochFromWp(b));

        const enriched = enrichWaypointsWithSensorData(sortedAll, tripList);
        const categorizedData = categorizeWaypoints(enriched);
        categorizedData.searchStart = searchParams?.start ? searchParams.start.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]') : '';
        categorizedData.searchEnd = searchParams?.end ? searchParams.end.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]') : '';

        if (onWaypointsLoaded) onWaypointsLoaded(categorizedData);
        prevWpIdRef.current = currentWpId;
      }
      return;
    }

    if (activePlayId) {
      const filteredWp = fullRawWaypoints.filter((wp) => {
        const wpEpoch = getEpochFromWp(wp);
        return wpEpoch >= activeTimeRange.start - 300000 && wpEpoch <= activeTimeRange.end + 300000;
      });

      const currentWpId = `filtered-${filteredWp.length}-${activePlayId}-${searchId}-${tripList.length}`;
      if (prevWpIdRef.current !== currentWpId) {
        const sortedFiltered = [...filteredWp].sort((a, b) => getEpochFromWp(a) - getEpochFromWp(b));
        const enriched = enrichWaypointsWithSensorData(sortedFiltered, tripList);
        const categorizedData = categorizeWaypoints(enriched);
        categorizedData.searchStart = searchParams?.start ? searchParams.start.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]') : '';
        categorizedData.searchEnd = searchParams?.end ? searchParams.end.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]') : '';

        if (onWaypointsLoaded) onWaypointsLoaded(categorizedData);
        prevWpIdRef.current = currentWpId;
      }
    }
  }, [fullRawWaypoints, activePlayId, activeTimeRange, isLoadingWp, isLoadingTrips, searchParams, tripList, onWaypointsLoaded, tz]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', bgcolor: '#fff' }}>
        {/* HEADER */}
        <Box sx={{ borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton size="small" onClick={onBack} sx={{ bgcolor: '#f0f0f0' }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box sx={{ color: '#2b2b2b' }}>
            <Typography variant="h4" fontWeight="bold">
              {t('mapsTracking.tripHistory', 'Trip History')}
            </Typography>
            <Typography variant="h6" color="text.secondary" noWrap sx={{ maxWidth: 200, display: 'block' }}>
              {vehicle?.fleet_name || vehicle?.vehicle_license_plate || 'Vehicle'}
            </Typography>
          </Box>
          <Box sx={{ ml: 'auto' }}>
            <Tooltip title={showPeriodConfig ? 'Hide Filter' : 'Show Filter'}>
              <IconButton
                onClick={() => setShowPeriodConfig(!showPeriodConfig)}
                size="small"
                sx={{
                  bgcolor: showPeriodConfig ? '#e2e8f0' : 'transparent',
                  color: showPeriodConfig ? '#1e293b' : '#64748b'
                }}
              >
                <FilterListIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ width: 1, mt: 1, flexShrink: 0 }}>
          {showPeriodConfig && (
            <Box sx={{ mb: 2 }}>
              <Stack spacing={1.5} mt={1}>
                <FormControl size="small" fullWidth>
                  <Select
                    value={rangeMode}
                    onChange={handleRangeModeChange}
                    displayEmpty
                    MenuProps={{
                      container: () => document.fullscreenElement || document.body,
                      sx: { zIndex: 100000 }
                    }}
                  >
                    <MenuItem value="today">{t('mapsTracking.today', 'Today')}</MenuItem>
                    <MenuItem value="lastWeek">{t('mapsTracking.lastWeek', 'Last Week')}</MenuItem>
                    <MenuItem value="custom">{t('mapsTracking.custom', 'Custom')}</MenuItem>
                  </Select>
                </FormControl>

                {rangeMode === 'custom' && (
                  <>
                    <DateTimePicker
                      timeSteps={{ minutes: 1 }}
                      label={t('mapsTracking.from', 'From')}
                      format="DD/MM/YYYY HH:mm:ss"
                      ampm={false}
                      maxDateTime={toDate || undefined}
                      value={fromDate}
                      onChange={(v) => {
                        const newV = v ? v.second(0) : v;
                        setFromDate(newV);
                        validateDateRange(newV, toDate);
                      }}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true, error: !!dateError },
                        popper: {
                          container: () => document.fullscreenElement || document.body,
                          placement: 'bottom-start',
                          modifiers: [
                            { name: 'preventOverflow', options: { boundary: 'window', padding: 8 } },
                            { name: 'flip', options: { fallbackPlacements: ['top-start', 'left-start', 'right-start'] } }
                          ],
                          sx: { zIndex: 100000 }
                        }
                      }}
                    />
                    <DateTimePicker
                      timeSteps={{ minutes: 1 }}
                      label={t('mapsTracking.to', 'To')}
                      format="DD/MM/YYYY HH:mm:ss"
                      ampm={false}
                      value={toDate}
                      minDateTime={fromDate || undefined}
                      onChange={(v) => {
                        const newV = v ? v.second(59) : v;
                        setToDate(newV);
                        validateDateRange(fromDate, newV);
                      }}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true, error: !!dateError },
                        popper: {
                          container: () => document.fullscreenElement || document.body,
                          placement: 'bottom-start',
                          modifiers: [
                            { name: 'preventOverflow', options: { boundary: 'window', padding: 8 } },
                            { name: 'flip', options: { fallbackPlacements: ['top-start', 'left-start', 'right-start'] } }
                          ],
                          sx: { zIndex: 100000 }
                        }
                      }}
                    />
                  </>
                )}

                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  onClick={handleSearch}
                  disabled={!!dateError || !fromDate || !toDate || isLoadingTrips || isLoadingWp}
                  startIcon={isLoadingTrips || isLoadingWp ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                  sx={{
                    bgcolor: '#485D9A',
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 'bold',
                    '&:hover': { bgcolor: '#3B4D82' },
                    '&.Mui-disabled': {
                      bgcolor: '#cfcfcf',
                      color: '#8f8f8f'
                    }
                  }}
                >
                  {isLoadingTrips || isLoadingWp ? t('mapsTracking.processing', 'Processing...') : t('mapsTracking.search', 'Search')}
                </Button>
              </Stack>

              {dateError && (
                <Typography variant="caption" color="error" textAlign="center">
                  {dateError}
                </Typography>
              )}
            </Box>
          )}
          {searchParams && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mt: 1,
                mb: 1,
                p: 1,
                bgcolor: '#f8fafc',
                borderRadius: 2,
                border: '1px solid #e2e8f0'
              }}
            >
              <Box sx={{ textAlign: 'left', flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }}>
                  {searchParams.start.format('DD MMM YYYY')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#2c3440ff' }}>
                  {searchParams.start.format('HH:mm:ss')}
                </Typography>
              </Box>

              <Box sx={{ px: 1, display: 'flex', alignItems: 'center', color: '#94a3b8' }}>
                <ArrowRightAltIcon fontSize="small" />
              </Box>

              <Box sx={{ textAlign: 'right', flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', lineHeight: 1.2 }}>
                  {searchParams.end.format('DD MMM YYYY')}
                </Typography>
                <Typography variant="caption" sx={{ color: '#2c3440ff' }}>
                  {searchParams.end.format('HH:mm:ss')}
                </Typography>
              </Box>
            </Box>
          )}

          {!searchParams && !isLoadingTrips && !isLoadingWp && (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('mapsTracking.pleaseSelectDateRange', 'Please select a date range and click the "Search" button to load data.')}
              </Typography>
            </Box>
          )}

          {(isLoadingTrips || isLoadingWp) && (
            <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
              {isLoadingTrips && loadingProgress > 0 ? (
                <>
                  <LinearProgress
                    variant="determinate"
                    value={loadingProgress}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      mb: 1.5,
                      bgcolor: 'rgba(72, 93, 154, 0.2)',
                      '& .MuiLinearProgress-bar': { bgcolor: '#485D9A' }
                    }}
                  />
                  <Typography variant="body2" color="text.secondary" fontWeight="bold">
                    {loadingProgress}% - {loadingText}
                  </Typography>
                </>
              ) : (
                <>
                  <CircularProgress size={30} sx={{ color: '#485D9A' }} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {t('mapsTracking.loadingTripData', 'Loading trip data...')}
                  </Typography>
                </>
              )}
            </Box>
          )}

          {isErrorWp && (
            <Box sx={{ py: 2, textAlign: 'center', color: 'error.main' }}>
              {t('mapsTracking.failedToLoadWaypointData', 'Failed to load waypoint track data.')}
            </Box>
          )}

          {(() => {
            if (isLoadingTrips || isLoadingWp || !searchParams) return null;

            const enrichedTripList = tripList.map((trip) => {
              const matchedRoute = allRoutes.find((r) => r.id === trip.route_trip_id);
              const isActive = matchedRoute ? matchedRoute.is_active === true || matchedRoute.is_active === 'true' : false;
              return { ...trip, isActive };
            });

            // const activeTripsCount = enrichedTripList.filter((t) => t.isActive).length;
            // const nonActiveTripsCount = enrichedTripList.filter((t) => !t.isActive).length;

            const filteredTripList = enrichedTripList.filter((trip) => {
              if (activeTab === 'Active Route') return trip.isActive;
              if (activeTab === 'Historical Route') return !trip.isActive;
              return true;
            });

            return (
              <Box sx={{ mb: 1.5, px: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#485D9A' }}>
                    {t('reports.total', 'Total:')} {filteredTripList.length}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      // startIcon={<VisibilityIcon />}
                      onClick={() => setShowRouteDialog(true)}
                      sx={{ textTransform: 'none', py: 0, height: 24, fontSize: '0.7rem' }}
                    >
                      {t('achievement.routeData', 'Route Data')}
                    </Button>
                    <Tooltip title="Information">
                      <IconButton onClick={() => setInfoOpen(true)} size="small" sx={{ p: 0.5, color: '#485D9A' }}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                <Tabs
                  value={activeTab}
                  onChange={(e, v) => setActiveTab(v)}
                  variant="fullWidth"
                  sx={{
                    minHeight: 36,
                    mt: 1,
                    bgcolor: '#f1f5f9',
                    borderRadius: '10px',
                    p: 0.5,
                    '& .MuiTabs-indicator': { display: 'none' },
                    '& .MuiTab-root': {
                      minHeight: 28,
                      py: 0.5,
                      px: 2,
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      color: '#64748b',
                      transition: 'all 0.2s',
                      '&.Mui-selected': {
                        bgcolor: '#ffffff',
                        color: '#1e3a8a',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }
                    }
                  }}
                >
                  <Tab label={t('dashboardAsset.all', 'All')} value="All" />
                  <Tab label={t('achievement.activeRoute', 'Active Route')} value="Active Route" />
                  <Tab label={t('achievement.historicalRoute', 'Historical Route')} value="Historical Route" />
                </Tabs>
              </Box>
            );
          })()}
        </Box>

        <Box
          sx={{
            flex: 1,
            width: 1,
            px: 1,
            overflow: 'auto',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' }
          }}
        >
          {!isLoadingTrips &&
            !isLoadingWp &&
            searchParams &&
            (() => {
              const enrichedTripList = tripList.map((trip) => {
                const matchedRoute = allRoutes.find((r) => r.id === trip.route_trip_id);
                const isActive = matchedRoute ? matchedRoute.is_active === true || matchedRoute.is_active === 'true' : false;
                return { ...trip, isActive };
              });

              const filteredTripList = enrichedTripList.filter((trip) => {
                if (activeTab === 'Active Route') return trip.isActive;
                if (activeTab === 'Historical Route') return !trip.isActive;
                return true;
              });

              return filteredTripList.map((tripItem, index) => {
                const tripId = `trip-${index}`;
                const expandedState = !!expanded[tripId];
                const isDetailsVisible = !!detailsExpanded[tripId];
                const playing = activePlayId === tripId;
                const isRouteActive = tripItem.isActive;
                const iconBg = isRouteActive ? (playing ? '#f44336' : '#485D9A') : playing ? '#f44336' : '#9E9E9E';

                return (
                  <Card
                    key={tripId}
                    className="trip-card"
                    variant="outlined"
                    sx={{ borderRadius: 2, border: playing ? '2px solid #485D9A' : '1px solid #e0e0e0', mb: 1, p: 1 }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Tooltip title={isRouteActive ? 'Active Route' : 'Historical Route'} placement="top">
                        <Box
                          onClick={() => togglePlay(tripItem, index)}
                          sx={{
                            background: iconBg,
                            borderRadius: 16,
                            width: 34,
                            height: 34,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            cursor: 'pointer',
                            color: '#fff'
                          }}
                        >
                          {playing ? <CloseIcon /> : <RouteIcon />}
                        </Box>
                      </Tooltip>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ mb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Chip
                            size="small"
                            label={`Ritase #${index + 1}`}
                            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 'bold', bgcolor: '#e2e8f0', color: '#475569' }}
                          />
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 'bold', color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5 }}
                          >
                            ⏱ {formatDurationStr(tripItem)}
                          </Typography>
                        </Box>
                        <Stack direction="row" spacing={1}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {t('time.start', 'Start')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                              {formatUI_DateTime(tripItem.start_time)}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              {t('time.end', 'End')}
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.75rem' }}>
                              {formatUI_DateTime(tripItem.end_time)}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                      <Box sx={{ cursor: 'pointer' }} onClick={(e) => toggleExpand(tripId, e)}>
                        {expandedState ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </Box>
                    </Box>

                    {expandedState && (
                      <Box sx={{ mt: 1 }}>
                        <Divider sx={{ my: 1 }} />
                        <Stack spacing={1}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption">{t('mapsTracking.driverPlate', 'Driver / Plate')}</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {tripItem.license_plate || '-'}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption">{t('mapsTracking.duration', 'Duration')}</Typography>
                            <Typography variant="caption" sx={{ fontWeight: 700 }}>
                              {formatDurationStr(tripItem)}
                            </Typography>
                          </Box>

                          <Button
                            size="small"
                            startIcon={<ListAltIcon />}
                            variant={isDetailsVisible ? 'contained' : 'outlined'}
                            sx={{ textTransform: 'none', mt: 0.5, py: 0.2 }}
                            onClick={(e) => toggleDetails(tripId, e)}
                          >
                            {isDetailsVisible
                              ? t('mapsTracking.hideRitaseDetails', 'Hide Ritase Details')
                              : t('mapsTracking.showRitaseDetails', 'Show Ritase Details')}
                          </Button>

                          {isDetailsVisible && (
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f8f9fa', borderRadius: 2, border: '1px solid #eeeeee' }}>
                              <Typography variant="caption" fontWeight="bold" color="primary" sx={{ display: 'block', mb: 1 }}>
                                {t('mapsTracking.routeCheckpoints', 'Route Checkpoints:')}
                              </Typography>

                              {tripItem.routes && tripItem.routes.length > 0 ? (
                                <Stack spacing={1.5}>
                                  {tripItem.routes.map((route, rIdx) => (
                                    <Box key={rIdx} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                      <Box
                                        sx={{
                                          width: 24,
                                          height: 24,
                                          borderRadius: '50%',
                                          bgcolor: '#485D9A',
                                          color: 'white',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          fontSize: '0.75rem',
                                          fontWeight: 'bold'
                                        }}
                                      >
                                        {route.sequence_number}
                                      </Box>
                                      <Box>
                                        <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                                          {route.spatial_name}
                                        </Typography>
                                        <Chip label={route.spatial_type} size="small" sx={{ height: 16, fontSize: '0.6rem', mt: 0.5 }} />
                                      </Box>
                                    </Box>
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  {t('mapsTracking.noRouteData', 'No route data.')}
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Stack>
                      </Box>
                    )}
                  </Card>
                );
              });
            })()}

          {!isLoadingTrips && !isLoadingWp && searchParams && tripList.length === 0 && !dateError && (
            <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary', fontSize: '0.85rem' }}>
              {t('mapsTracking.noRitaseTripData', 'No ritase trip data for this period.')}
            </Box>
          )}
        </Box>

        {/* Sticky Bottom Summary */}
        {!isLoadingTrips &&
          !isLoadingWp &&
          searchParams &&
          (() => {
            const enrichedTripList = tripList.map((trip) => {
              const matchedRoute = allRoutes.find((r) => r.id === trip.route_trip_id);
              const isActive = matchedRoute ? matchedRoute.is_active === true || matchedRoute.is_active === 'true' : false;
              return { ...trip, isActive };
            });
            const activeTripsCount = enrichedTripList.filter((t) => t.isActive).length;
            const nonActiveTripsCount = enrichedTripList.filter((t) => !t.isActive).length;

            return (
              <Box
                sx={{
                  bgcolor: '#ffffff',
                  borderTop: '1px solid #e2e8f0',
                  pt: 1,
                  pb: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 4,
                  zIndex: 10
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box
                    sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#485D9A', boxShadow: '0 0 0 2px rgba(72, 93, 154, 0.2)' }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>
                    {activeTripsCount} Active
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Box
                    sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#9E9E9E', boxShadow: '0 0 0 2px rgba(158, 158, 158, 0.2)' }}
                  />
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#475569' }}>
                    {nonActiveTripsCount} Historical
                  </Typography>
                </Box>
              </Box>
            );
          })()}
      </Box>

      <Dialog
        open={showRouteDialog}
        onClose={() => setShowRouteDialog(false)}
        maxWidth="lg"
        fullWidth
        sx={{ '& .MuiDialog-paper': { height: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography variant="h6" fontWeight="bold">
            Master Route
          </Typography>
          <IconButton onClick={() => setShowRouteDialog(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <MasterRoutePage />
        </DialogContent>
      </Dialog>

      <GuideRouteDialog open={infoOpen} onClose={() => setInfoOpen(false)} />
    </LocalizationProvider>
  );
}
